import React, { Suspense } from 'react';
import { Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from './routes/ProtectedRoute';
import PublicRoute from './routes/PublicRoute';
import CreatorRoute from './routes/CreatorRoute';
import { useAuth } from './context/AuthContext';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import { VERIFY_EMAIL_URL, PASSWORD_RESET_URL, SINGLE_POST_PAGE_URL, PAYMENT_SUCCESS_URL, PAYMENT_DECLINED_URL, HOME_PAGE_URL_ALIASES, LOGIN_PAGE_URL, CIRCLES_PAGE_URL, DASHBOARD_PAGE_URL, SETTINGS_PAGE_URL, HOME_PAGE_URL, CORE_VIP, FEED_PAGE_URL, SEARCH_PAGE_URL, NEW_MESSAGE_PAGE_URL, NOTIFICATIONS_PAGE_URL, MESSAGE_PAGE_URL, SCHEDULED_PAGE_URL, MEDIA_LIBRARY_PAGE_URL, CATEGORIES_PAGE_URL } from './constants/systemConstants';

// Lazy load all page-level components
const HomePage = React.lazy(() => import('./pages/HomePage'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Notifications = React.lazy(() => import('./pages/Notifications'));
const Messages = React.lazy(() => import('./pages/Messages'));
const Circles = React.lazy(() => import('./pages/Circles'));
const Settings = React.lazy(() => import('./pages/Settings'));
const Search = React.lazy(() => import('./pages/Search'));
const Login = React.lazy(() => import('./pages/Login'));
const NewMessage = React.lazy(() => import('./pages/NewMessage'));
const SinglePost = React.lazy(() => import('./pages/SinglePost'));
const PaymentResult = React.lazy(() => import('./components/Payment/PaymentResult'));
const PasswordResetHandler = React.lazy(() => import('./components/Auth/PasswordResetHandler'));
const PasswordResetQueryHandler = React.lazy(() => import('./components/Auth/PasswordResetQueryHandler'));
const ProfileRouteGuard = React.lazy(() => import('./components/Profile/ProfileRouteGuard'));
const EmailVerification = React.lazy(() => import('./pages/EmailVerification'));
const MyPurchases = React.lazy(() => import('./pages/MyPurchases'));
const MySubscriptions = React.lazy(() => import('./pages/MySubscriptions'));
const Scheduled = React.lazy(() => import('./pages/Scheduled'));
const Categories = React.lazy(() => import('./pages/Categories'));
const MediaLibrary = React.lazy(() => import('./pages/MediaLibrary'));
const FinishSignup = React.lazy(() => import('./pages/FinishSignup'));

const Routes = () => {
  const { loading } = useAuth();

  // Force re-evaluation when location changes to ensure proper navigation

  if (loading) {
    return (
      <LoadingSpinner 
        fullPage 
        size={40}
        text="Loading..."
      />
    );
  }

  return (
    <Suspense fallback={<LoadingSpinner fullPage size={40} text="Loading..." />}>
    <RouterRoutes>
      {/* Login route - accessible only when not logged in */}
      <Route path={LOGIN_PAGE_URL} element={<PublicRoute><Login /></PublicRoute>} />
      
      {/* Password reset token handler - redirects to login with modal */}
      <Route path={`${PASSWORD_RESET_URL}/:token`} element={<PublicRoute><PasswordResetHandler /></PublicRoute>} />
      
      {/* Password reset query parameter handler - for backward compatibility */}
      <Route path={PASSWORD_RESET_URL} element={<PublicRoute><PasswordResetQueryHandler /></PublicRoute>} />

      {/* Email verification route - accessible to both logged in and logged out users */}
      <Route path={VERIFY_EMAIL_URL} element={<EmailVerification />} />

      {/* Social-login: finish signup with username (after OAuth callback) */}
      <Route path="/finish-signup" element={<FinishSignup />} />

      {/* Core VIP app routes - ALL app features under /vip/ namespace */}
      <Route path={HOME_PAGE_URL} element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      <Route path={`/${CORE_VIP}`} element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      <Route path={HOME_PAGE_URL_ALIASES[0]} element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      <Route path={HOME_PAGE_URL_ALIASES[1]} element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      <Route path={`${FEED_PAGE_URL}`} element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      <Route path={`${SEARCH_PAGE_URL}`} element={<ProtectedRoute><Search /></ProtectedRoute>} />
      {/* Dashboard - Creator only with canAccessDashboard check */}
      <Route path={`${DASHBOARD_PAGE_URL}`} element={
        <ProtectedRoute>
          <CreatorRoute feature="canAccessDashboard">
            <Dashboard />
          </CreatorRoute>
        </ProtectedRoute>
      } />
      <Route path={`${NOTIFICATIONS_PAGE_URL}`} element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      <Route path={`${MESSAGE_PAGE_URL}`} element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path={`${MESSAGE_PAGE_URL}/:conversationId`} element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path={`${NEW_MESSAGE_PAGE_URL}`} element={<ProtectedRoute><NewMessage /></ProtectedRoute>} />
      <Route path={`${CIRCLES_PAGE_URL}`} element={<ProtectedRoute><Circles /></ProtectedRoute>} />
      <Route path="/vip/lists" element={<Navigate to={CIRCLES_PAGE_URL} replace />} /> {/* Redirect old URL */}
      <Route path="/vip/purchases" element={<ProtectedRoute><MyPurchases /></ProtectedRoute>} /> {/* My Purchases library */}
      <Route path="/vip/subscriptions" element={<ProtectedRoute><MySubscriptions /></ProtectedRoute>} /> {/* My Subscriptions */}
      <Route path={SCHEDULED_PAGE_URL} element={<ProtectedRoute><CreatorRoute><Scheduled /></CreatorRoute></ProtectedRoute>} />
      <Route path={CATEGORIES_PAGE_URL} element={<ProtectedRoute><CreatorRoute><Categories /></CreatorRoute></ProtectedRoute>} />
      <Route path={MEDIA_LIBRARY_PAGE_URL} element={<ProtectedRoute><CreatorRoute><MediaLibrary /></CreatorRoute></ProtectedRoute>} />
      <Route path={`${SETTINGS_PAGE_URL}`} element={<ProtectedRoute><Settings /></ProtectedRoute>} />

      {/* Single post page */}
      <Route path={`${SINGLE_POST_PAGE_URL}/:postId`} element={<ProtectedRoute><SinglePost /></ProtectedRoute>} />

      {/* Payment result routes */}
      <Route path={PAYMENT_SUCCESS_URL} element={<PaymentResult />} />
      <Route path={PAYMENT_DECLINED_URL} element={<PaymentResult />} />
      
      {/* Clean profile routes - Twitter/Instagram style - PUBLIC (semi-public) */}
      <Route path="/:username" element={<ProfileRouteGuard />} />

      {/* Catch all redirect to home */}
      <Route path="*" element={<Navigate to={HOME_PAGE_URL} replace />} />
    </RouterRoutes>
    </Suspense>
  );
}

export default Routes;